import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../../../theme";
import { FeatureItem } from "./FeaturesCard";
import { getGoogleCalendarUrl } from "../../stream-management/components/memorial-page/components/types/AddtoCalendarutils";

interface FeatureDetailsModalProps {
  open: boolean;
  feature: FeatureItem | null;
  onClose: () => void;
}

const FeatureDetailsModal = ({
  open,
  feature,
  onClose,
}: FeatureDetailsModalProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (!feature) return null;

  const statusColor =
    feature.status === "overdue"
      ? "#E11D48"
      : feature.status === "in-progress"
        ? "#f59e0b"
        : "#10b981";

  const statusLabel =
    feature.status === "in-progress"
      ? "In Progress"
      : feature.status === "overdue"
        ? "Overdue"
        : "Complete";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          backgroundColor: colors.primary[900],
          border: `1px solid ${colors.primary[300]}30`,
          minWidth: 340,
        },
      }}
    >
      {/* Title Header Section */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.95rem",
            color: colors.primary[100],
          }}
        >
          Feature Details
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 16, color: colors.primary[600] }} />
        </IconButton>
      </DialogTitle>

      {/* Content Details Section */}
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pt: "8px !important",
        }}
      >
        {/* Feature Name */}
        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: colors.primary[600],
              mb: 0.5,
              letterSpacing: "0.08em",
            }}
          >
            FEATURE NAME
          </Typography>
          <Typography
            sx={{
              fontSize: "0.9rem",
              fontWeight: 600,
              color: colors.primary[100],
            }}
          >
            {feature.name}
          </Typography>
        </Box>

        {/* User Lead / Assigned Developer */}
        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: colors.primary[600],
              mb: 0.8,
              letterSpacing: "0.08em",
            }}
          >
            USER LEAD
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={feature.leadPicture || undefined}
              sx={{
                width: 36,
                height: 36,
                border: `1.5px solid ${colors.primary[400]}`,
              }}
            >
              {feature.lead[0]}
            </Avatar>
            <Typography
              sx={{ fontSize: "0.85rem", color: colors.primary[100] }}
            >
              {feature.lead}
            </Typography>
          </Box>
        </Box>

        {/* Staging Date */}
        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: colors.primary[600],
              mb: 0.5,
              letterSpacing: "0.08em",
            }}
          >
            STAGING DATE
          </Typography>
          <Typography
            sx={{
              fontSize: "0.88rem",
              fontWeight: 600,
              color: colors.primary[100],
            }}
          >
            {feature.stagingDate}
          </Typography>
        </Box>

        {/* Live Date */}
        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: colors.primary[600],
              mb: 0.5,
              letterSpacing: "0.08em",
            }}
          >
            LIVE DATE
          </Typography>
          <Typography
            sx={{
              fontSize: "0.88rem",
              fontWeight: 600,
              color: colors.primary[100],
            }}
          >
            {feature.liveDate}
          </Typography>
        </Box>

        {/* Status Chip */}
        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: colors.primary[600],
              mb: 0.5,
              letterSpacing: "0.08em",
            }}
          >
            STATUS
          </Typography>
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              backgroundColor: statusColor + "20",
              color: statusColor,
              fontWeight: 600,
            }}
          />
        </Box>

        {/* Calendar Link Action */}
        <Box
          sx={{ mt: 2, pt: 1, borderTop: `1px solid ${colors.primary[300]}15` }}
        >
          <Typography
            onClick={() => {
              window.open(
                getGoogleCalendarUrl({
                  title: `${feature.name} - Live Launch`,
                  description: `Live launch of ${feature.name}, led by ${feature.lead}`,
                  startDate: new Date(`${feature.liveDate}, 2026`),
                  endDate: new Date(
                    new Date(`${feature.liveDate}, 2026`).getTime() +
                      60 * 60 * 1000,
                  ),
                }),
                "_blank",
                "noopener,noreferrer",
              );
            }}
            sx={{
              cursor: "pointer",
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "#1976d2",
              textAlign: "center",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            + Add launch date to calendar
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FeatureDetailsModal;
